import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { api } from "../lib/api";

export const Route = createFileRoute("/compile-run/$compileId")({
  component: CompileRunDetailsPage,
  loader: async ({ params }) => {
    const compileRun = await api.getCompileRun(params.compileId);
    return { compileRun };
  },
});

interface TypeScriptError {
  message: string;
  severity: string;
  location: string;
}

function CompileRunDetailsPage() {
  const { compileRun } = Route.useLoaderData();
  const navigate = useNavigate({ from: Route.fullPath });

  const formatDate = (timestamp: string | number) => {
    const date =
      typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleString();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "error":
        return "text-red-600 bg-red-100";
      case "warning":
        return "text-yellow-600 bg-yellow-100";
      case "info":
        return "text-blue-600 bg-blue-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  // Parse error JSON if available
  let parsedErrors: TypeScriptError[] = [];
  let errorJsonParseError = false;

  if (compileRun.errorsJson) {
    try {
      parsedErrors = JSON.parse(compileRun.errorsJson);
    } catch (error) {
      console.error("Failed to parse error JSON:", error);
      errorJsonParseError = true;
    }
  }

  const ProjectIdLink = ({ projectId }: { projectId?: string }) => {
    if (!projectId) {
      return <span className="text-gray-400">N/A</span>;
    }

    // Try to create a link to the project if it looks like a numeric ID
    if (/^\d+$/.test(projectId)) {
      return (
        <a
          href={`https://app.fiberplane.com/projects/${projectId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-600 hover:text-indigo-800 underline"
        >
          {projectId}
        </a>
      );
    }

    return <span>{projectId}</span>;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Compile Run Details: {compileRun.compileId}
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Detailed information and errors for this compilation run
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: "/typescript-analysis",
                  search: { tab: "browse" },
                })
              }
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              ← Back to Analysis
            </button>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Timestamp</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(compileRun.timestamp)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">User ID</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {compileRun.userId || "N/A"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Project ID
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <ProjectIdLink projectId={compileRun.projectId} />
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Error Count
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {compileRun.errorCount}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Version</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {compileRun.honcVersion || "N/A"}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Error Display */}
        {compileRun.errorCount > 0 && (
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                TypeScript Errors ({compileRun.errorCount})
              </h3>
            </div>
            <div className="border-t border-gray-200">
              {errorJsonParseError ? (
                // Show raw JSON if parsing failed
                <div className="p-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Raw Error Data
                  </h4>
                  <pre className="bg-gray-50 border border-gray-200 rounded-md p-4 text-xs overflow-auto max-h-96 whitespace-pre-wrap">
                    {compileRun.errorsJson}
                  </pre>
                </div>
              ) : parsedErrors.length > 0 ? (
                // Show parsed errors in a friendly format
                <ul className="divide-y divide-gray-200">
                  {parsedErrors.map((error, index) => (
                    <li key={`error-${index}`} className="px-6 py-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mr-3 ${getSeverityColor(error.severity)}`}
                            >
                              {error.severity}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {error.location}
                            </span>
                          </div>
                          <p className="mt-2 text-sm text-gray-700 font-mono">
                            {error.message}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                // No errors found in JSON
                <div className="px-6 py-4 text-sm text-gray-500">
                  No error details available.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Success State */}
        {compileRun.errorCount === 0 && (
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold text-sm">✓</span>
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-gray-900">
                    Compilation Successful
                  </h3>
                  <p className="text-sm text-gray-500">
                    This compilation run completed without any TypeScript
                    errors.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
